import { Injectable, inject } from '@angular/core';

import { LiveHighlightsResponse } from '@cds/betting-offer/domain-specific/live';
import { MessageEnvelope, MessageType } from '@cds/push';
import { FixtureState, LiveStreamingRequest, OfferMapping, ScoreboardMode, SortByCriteria } from '@cds/query-objects';
import { DetailedFixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventMediaHelperService } from '@frontend/sports/betting-offer/feature/media';
import { EventDetailsColumnType, EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { ConnectionConfig, MediaConfig } from '@frontend/sports/common/client-config-data-access';
import { noopAction } from '@frontend/sports/common/core/utils/redux';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { CdsPushService, ScoreboardSubscription } from '@frontend/sports/content-distribution/feature';
import {
    EventSwitcherItem,
    MediaEventSource,
    MediaModuleContextPage,
    MediaPersistentState,
    MediaSetEvent,
    MediaSetEventInternal,
} from '@frontend/sports/grid/media/feature/model';
import {
    IMediaRootState,
    MediaApiActions,
    MediaState,
    mediaActiveSelector,
    mediaEventInfoSelector,
    mediaExpandedStateSelector,
    mediaStateSelector,
} from '@frontend/sports/grid/media/feature/state';
import { ColumnLayoutRegister, ColumnSize, LayoutColumn } from '@frontend/sports/layout/feature';
import { TrackingVideoSource } from '@frontend/sports/tracking/feature';
import { UserService } from '@frontend/sports/user/feature';
import { LocalStoreService, MediaQueryService, UserLoginEvent } from '@frontend/vanilla/core';
import { Actions as EffectActions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { applyPatch } from 'fast-json-patch';
import { keyBy, omit, uniq } from 'lodash-es';
import { concatMap, filter, from, map, of, skipWhile, switchMap, takeUntil, tap, timer, withLatestFrom } from 'rxjs';

import { EventDetailsHelperService } from '../event-details-common/event-details-helper.service';
import { EventDetailsService } from '../event-details-common/event-details.service';
import { StoragePersister } from '../store-persist/storage-persister';
import { Actions } from './actions';
import { MediaStateLoader, isMediaSavedState } from './media-state-storage';
import { UnpinInfoService } from './unpin-info.service';

export interface MediaComponentState {
    video: { eventId: number; pinned: boolean; autoplay: boolean };
    animation: { eventId: number };
    statistics: { eventId: number };
    activeTab: EventDetailsColumnType;
}

@Injectable({ providedIn: 'root' })
export class MediaEffects implements OnInitEffects {
    private actions$ = inject(EffectActions);
    private store = inject<Store<IMediaRootState>>(Store);
    private cdsSubscription = inject(CdsPushService);
    private bettingOfferApi = inject(BettingOfferApi);
    private eventDetailsService = inject(EventDetailsService);
    private detailedFixtureFactory = inject(DetailedFixtureFactory);
    private eventDetailsHelper = inject(EventDetailsHelperService);
    private eventMediaHelperService = inject(EventMediaHelperService);
    private unpinInfoService = inject(UnpinInfoService);
    private userService = inject(UserService);
    private localStoreService = inject(LocalStoreService);
    private columnLayoutRegister = inject(ColumnLayoutRegister);
    private mediaConfig = inject(MediaConfig);
    private mediaService = inject(MediaQueryService);
    private connection = inject(ConnectionConfig);
    private storagePersister = inject(StoragePersister);
    private stateLoader = inject(MediaStateLoader);

    private static readonly STREAMS_CACHE_INTERVAL = 2 * 60 * 1000;
    private static readonly MEDIA_PERSISTENT_STATE_KEY = 'media-state';

    private isStateRestored = false;
    private subscriptions: { [eventId: number | string]: { unsubscribe: () => void } } = {};

    onInit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Actions.init),
            concatMap(() => [
                Actions.toggleMediaModule({ value: true }),
                Actions.startFetchVideoEvents(),
                Actions.startUserServiceEventsListener(),
                Actions.pinEnabledStateToggle({ value: this.userService.isAuthenticated }),
                Actions.startRestoreState(),
            ]),
        );
    });

    setEventId$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MediaApiActions.setEventId),
            concatMap((action) => of(action).pipe(withLatestFrom(this.store.select(mediaEventInfoSelector(action.payload.eventId))))),
            switchMap(([action, eventInfo]) => {
                const eventModel$ = eventInfo
                    ? of(eventInfo.event)
                    : from(this.eventDetailsService.getEvent(action.payload.eventId.toString(), true));

                return eventModel$.pipe(
                    filter((event) => event !== undefined),
                    map((result) => ({
                        event: result!,
                        tab: action.payload.tab,
                        source: action.payload.source,
                        trackingVideoSource: action.payload.trackingVideoSource,
                    })),
                );
            }),
            map((actionPayload) => Actions.setEventInternal({ payload: this.mapMediaSetEventToSetEventInternalPayload(actionPayload) })),
        );
    });

    setEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MediaApiActions.setEvent),
            map((action) => Actions.setEventInternal({ payload: this.mapMediaSetEventToSetEventInternalPayload(action.payload) })),
        );
    });

    setEventInternal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Actions.setEventInternal),
            withLatestFrom(this.store.select(mediaStateSelector)),
            tap(([action, mediaState]) => this.updateSubscriptions(mediaState, action.payload.event)),
            concatMap(([action, mediaState]) => this.dispatchSetEventInternalActions(action.payload, mediaState)),
        );
    });

    unsetEvent$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(Actions.unsetEvent),
                withLatestFrom(this.store.select(mediaStateSelector)),
                tap(([action, mediaState]) => this.removeObsoleteSubscriptions(mediaState)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    startFetchVideoEvents$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Actions.startFetchVideoEvents),
            switchMap(() => {
                const notActive$ = this.store.select(mediaActiveSelector).pipe(skipWhile((active) => !!active));

                return timer(0, MediaEffects.STREAMS_CACHE_INTERVAL).pipe(
                    map(() => Actions.fetchVideoEvents()),
                    takeUntil(notActive$),
                );
            }),
        );
    });

    startUserServiceEventsListener$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Actions.startUserServiceEventsListener),
            switchMap(() => {
                const notActive$ = this.store.select(mediaActiveSelector).pipe(skipWhile((active) => active));

                return this.userService.onAuthenticationChange$.pipe(
                    map((event) => Actions.pinEnabledStateToggle({ value: event instanceof UserLoginEvent })),
                    takeUntil(notActive$),
                );
            }),
        );
    });

    fetchVideoEvents$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Actions.fetchVideoEvents),
            switchMap(() => {
                const request: LiveStreamingRequest = {
                    state: FixtureState.Latest,
                    offerMapping: OfferMapping.None,
                    scoreboardMode: ScoreboardMode.None,
                    sortBy: SortByCriteria.Tags,
                    maxFixturesPerSport: undefined,
                    includePlayableOnly: true,
                } as any;

                return this.bettingOfferApi.getLiveStreams(request);
            }),
            map((streams) => Actions.setVideoSwitcherContent({ content: this.mapToEventSwitcherItem(streams) })),
        );
    });

    acceptUnpinInfoMessage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Actions.acceptUnpinInfoMessage),
            tap(() => this.unpinInfoService.acceptUnpinInfoMessage()),
            map(() => Actions.toggleUnpinInfoMessage({ value: false })),
        );
    });

    toggleUnpinInfoMessageAfterContextSwitch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MediaApiActions.setMediaWidgetContext),
            filter((action) => action.payload.page === MediaModuleContextPage.Default),
            map(() => Actions.toggleUnpinInfoMessage({ value: false })),
        );
    });

    toggleUnpinInfoMessageAfterUnpinning$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Actions.togglePin),
            filter((action) => !action.value),
            map(() => Actions.toggleUnpinInfoMessage({ value: false })),
        );
    });

    persistStateEpic$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    Actions.unsetEvent,
                    Actions.setEventInternal,
                    Actions.setActiveTab,
                    Actions.toggleAutoplay,
                    Actions.togglePin,
                    Actions.restoreState,
                    Actions.resetState,
                ),
                withLatestFrom(this.store.select(mediaStateSelector)),
                tap(([action, mediaState]) => this.persistState(mediaState)),
            );
        },
        { dispatch: false },
    );

    startRestoreStateEpic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Actions.startRestoreState),
            switchMap(() => {
                const mediaPersistentState = this.localStoreService.get(MediaEffects.MEDIA_PERSISTENT_STATE_KEY) as MediaPersistentState;
                if (!mediaPersistentState) {
                    this.isStateRestored = true;

                    return of(null);
                }

                const eventIds = uniq([
                    mediaPersistentState.video.eventId,
                    mediaPersistentState.animation.eventId,
                    mediaPersistentState.statistics.eventId,
                ]).filter((eventId) => !!eventId);

                return from(Promise.all(eventIds.map((eventId) => this.eventDetailsService.getEvent(eventId!.toString(), true)))).pipe(
                    map((events: (EventModel | undefined)[]) => {
                        const eventsMap = keyBy(
                            events.filter((event) => !!event).map((event) => this.mapEventModelToSetEventInternalPayload(event!)),
                            'event.id',
                        );

                        return { ...mediaPersistentState, events: eventsMap };
                    }),
                );
            }),
            filter((payload) => !!payload),
            map((payload) => Actions.restoreState({ payload: payload! })),
        );
    });

    restoreState$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(Actions.restoreState),
                tap(() => {
                    this.isStateRestored = true;
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    toggleExpand$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(Actions.toggleExpand),
                withLatestFrom(this.store.select(mediaExpandedStateSelector)),
                tap(([action, expandedState]) => {
                    const isExpanded = expandedState.isExpanded;
                    const layoutSize = isExpanded
                        ? this.mediaService.isActive('gt-mw')
                            ? ColumnSize.ExtraLarge
                            : ColumnSize.Large
                        : ColumnSize.Default;
                    this.columnLayoutRegister.resizeColumn(layoutSize, LayoutColumn.Right);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handlePopulateFromStorage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(Actions.populateFromStorage),
                withLatestFrom(this.store.select(mediaExpandedStateSelector)),
                tap(([action, expandedState]) => {
                    const restoreExpandState = action.state.mediaExpandState.restoreExpandState;
                    const isLocationAllowsExpand = expandedState.isLocationAllowsExpand;

                    if (isLocationAllowsExpand && restoreExpandState) {
                        this.columnLayoutRegister.resizeColumn(
                            this.mediaService.isActive('gt-mw') ? ColumnSize.ExtraLarge : ColumnSize.Large,
                            LayoutColumn.Right,
                        );
                    }
                }),
            );
        },
        { dispatch: false },
    );

    handlebreakpointChange$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(Actions.breakpointChanged),
                withLatestFrom(this.store.select(mediaExpandedStateSelector)),
                tap(([action, expandedState]) => {
                    const isMediaExpanded = expandedState.isExpanded;

                    if (isMediaExpanded) {
                        this.columnLayoutRegister.resizeColumn(
                            this.mediaService.isActive('gt-mw') ? ColumnSize.ExtraLarge : ColumnSize.Large,
                            LayoutColumn.Right,
                        );
                    } else {
                        this.columnLayoutRegister.resizeColumn(ColumnSize.Default, LayoutColumn.Right);
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    ngrxOnInitEffects(): Action {
        const state = this.storagePersister.load();
        if (isMediaSavedState(state)) {
            const mediaState = this.stateLoader.load(state.media);

            return Actions.populateFromStorage({ state: mediaState });
        }

        return noopAction;
    }

    private *dispatchSetEventInternalActions(payload: MediaSetEventInternal, mediaState: MediaState): Iterable<Action> {
        yield Actions.toggleUnpinInfoMessage({ value: this.unpinInfoService.shouldShowUnpinInfoMessage(mediaState) });

        if (
            payload.source === MediaEventSource.Grid ||
            payload.source === MediaEventSource.Scoreboard ||
            payload.source === MediaEventSource.EventDetails
        ) {
            yield Actions.toggleCollapsedState({ value: false });
        }
    }

    private mapToEventSwitcherItem(eventsModel?: LiveHighlightsResponse | undefined): EventSwitcherItem[] {
        if (!eventsModel) {
            return [];
        }

        return eventsModel.sportsOffer
            .filter((sportOffer) => !this.mediaConfig.restrictedSportIds.includes(sportOffer.sport.id))
            .map((item) => ({
                id: item.sport.id,
                name: item.sport.name.value,
                children: item.fixtures.map((event) => ({
                    id: event.id,
                    name: event.name.value,
                })),
            }));
    }

    private updateSubscriptions(mediaState: MediaState, event: EventModel): void {
        this.removeObsoleteSubscriptions(mediaState);
        this.subscribe(event);
    }

    private persistState(mediaState: MediaState): void {
        if (!this.isStateRestored) {
            return;
        }
        const persistedState: MediaPersistentState = {
            activeTab: mediaState.activeTab,
            video: {
                eventId: mediaState.video.eventId,
                pinned: mediaState.video.pinned,
            },
            animation: mediaState.animation,
            statistics: mediaState.statistics,
        };

        this.localStoreService.set(MediaEffects.MEDIA_PERSISTENT_STATE_KEY, persistedState);
    }

    private removeObsoleteSubscriptions(mediaState: MediaState): void {
        const toUnsubscribe = omit(this.subscriptions, [
            mediaState.video.eventId || 0,
            mediaState.animation.eventId || 0,
            mediaState.statistics.eventId || 0,
        ]);

        Object.keys(toUnsubscribe).forEach((eventId) => {
            if (this.subscriptions[eventId]) {
                this.subscriptions[eventId].unsubscribe();
                delete this.subscriptions[eventId];
            }
        });
    }

    private subscribe(event: EventModel): void {
        if (this.subscriptions[event.id] || !event.live) {
            return;
        }

        const scoreboardHandler = (pushData: MessageEnvelope) => {
            if (pushData.messageType === MessageType.PlayerStatsUpdate) {
                return;
            }

            applyPatch(event.fixtureScoreboard, pushData.payload.patch.operations);
            this.detailedFixtureFactory.updateScoreboard(event.fixtureScoreboard, event);
            this.updateScoreboard(event);
        };

        const scoreboardSubscription = new ScoreboardSubscription(event.offerContext, event.id, this.connection.culture, scoreboardHandler);

        this.cdsSubscription.subscribe(scoreboardSubscription);

        this.subscriptions[event.id] = { unsubscribe: () => this.cdsSubscription.unsubscribe(scoreboardSubscription) };
    }

    private mapMediaSetEventToSetEventInternalPayload(setEventPayload: MediaSetEvent): MediaSetEventInternal {
        return {
            event: setEventPayload.event,
            tab: setEventPayload.tab,
            source: setEventPayload.source,
            hasVideo: this.eventMediaHelperService.hasLiveVideo(setEventPayload.event),
            hasAnimation: this.eventMediaHelperService.hasAnimationOrSimulation(setEventPayload.event),
            hasStats: this.hasStats(setEventPayload.event),
            showStatsCenterButton: this.mediaConfig.showStatsCenterButton,
            trackingVideoSource: setEventPayload.trackingVideoSource,
        };
    }

    private mapEventModelToSetEventInternalPayload(eventModel: EventModel): MediaSetEventInternal {
        return {
            event: eventModel,
            tab: null,
            source: MediaEventSource.Default,
            hasVideo: this.eventMediaHelperService.hasLiveVideo(eventModel),
            hasAnimation: this.eventMediaHelperService.hasAnimationOrSimulation(eventModel),
            hasStats: this.hasStats(eventModel),
            showStatsCenterButton: this.mediaConfig.showStatsCenterButton,
            trackingVideoSource: TrackingVideoSource.Default,
        };
    }

    private updateScoreboard(event: EventModel): void {
        this.store.dispatch(Actions.updateScoreboard({ model: event }));
    }

    private hasStats(eventModel: EventModel): boolean {
        return (
            (this.eventDetailsHelper.hasStats(eventModel) || (!!eventModel.statsId && this.mediaConfig.showStatsCenterButton)) &&
            !this.eventMediaHelperService.hasPerformPlayerEnabled(eventModel)
        );
    }
}
